import { lazy, Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { LoadingScreen } from '@/components/loading-screen';
import { USER_TYPE } from '@/lib/data/domains/user';
import { getPagePermissions } from '@/lib/utils/get-page-permissions';
import { useAuthStore } from '@/store/auth';

// FMICE-812 W10: the dashboards load on demand, not with the entry chunk.
const Dashboard = lazy(() => import('@/components/dashboard/dashboard'));
const FabricatorDashboard = lazy(
  () => import('@/pages/fabricator-dashboard/fabricator-dashboard'),
);

export default function HomePage() {
  // Component hooks
  const navigate = useNavigate();

  // Component stores
  const { user } = useAuthStore();

  // Component states - chart loading statuses
  const [isLoading, setIsLoading] = useState(true);

  // Component defaults
  const claimsPagePermissions = getPagePermissions(user!, 'claims');
  const fmiLeadsPagePermissions = getPagePermissions(user!, 'fmi-leads');
  const jobsPagePermissions = getPagePermissions(user!, 'jobs');

  // Component side effect - navigate users to relevant page
  useEffect(() => {
    if (user?.permissions.dashboard) {
      setIsLoading(false);

      return;
    }

    // Every user type lands on the first page it can actually read when its
    // default landing page is not granted. Without this the hard-coded
    // destinations below send the user straight to /403 — e.g. a fabricator
    // whose role carries action-items but not claims.
    const navigateToFirstAvailablePage = () => {
      const firstAvailablePage = user?.permissions.web
        ?.flatMap((group) => group.children ?? [])
        .find((page) => page.resourceScope?.read?.isChecked);

      if (firstAvailablePage) {
        navigate(`/${firstAvailablePage.resourcePath}`, { replace: true });

        return;
      }

      setIsLoading(false);
    };

    if (user?.userType) {
      if (
        user?.userType === USER_TYPE.CUSTOMER ||
        user?.userType === USER_TYPE.FABRICATOR
      ) {
        if (claimsPagePermissions?.read) {
          navigate('/claims', { replace: true });
        } else {
          navigateToFirstAvailablePage();
        }

        return;
      }

      if (user?.userType === USER_TYPE.GLAZIER) {
        if (jobsPagePermissions?.read) {
          navigate('/jobs', { replace: true });
        } else {
          navigateToFirstAvailablePage();
        }

        return;
      }

      if (
        user?.userType === USER_TYPE.FMI ||
        user?.userType === USER_TYPE.SUPPLIER
      ) {
        if (claimsPagePermissions?.read) {
          navigate('/claims', { replace: true });
        } else if (fmiLeadsPagePermissions?.read) {
          navigate('/fmi-leads', { replace: true });
        } else {
          navigateToFirstAvailablePage();
        }

        return;
      }

      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className='home-page'>
      {!isLoading ? (
        <Suspense fallback={<LoadingScreen />}>
          {user?.userType === USER_TYPE.FABRICATOR ? (
            <FabricatorDashboard />
          ) : (
            <Dashboard />
          )}
        </Suspense>
      ) : (
        <LoadingScreen />
      )}
    </section>
  );
}
